import { observer } from 'mobx-react-lite';
import React, { useCallback } from 'react';
import styles from '../program-sheet.module.scss';
import clsx from 'clsx';

import { FocusContext, useFocusable } from '@noriginmedia/norigin-spatial-navigation';
import Skeleton from '@smart-tv/components/skeleton';
import Typography from '@smart-tv/components/typography';
import testIds from '@smart-tv/e2e/shared/variables';

import { rootStore } from '../_stores/store-root';
import { ExtendedDescriptionDataProvider, uiStore } from '../_stores/store-ui';
import { FocusKeysSheet } from '../sheet-helpers';
import SheetDescriptionInfoAirtime from './sheet-description-info-airtime';
import SheetDescriptionInfoExpiry from './sheet-description-info-expiry';
import SheetDescriptionInfoMeta from './sheet-description-info-meta';

const SheetDescription = observer(() => {
  const sheet = rootStore.sheetStore.sheet;
  const fetchGeneration = rootStore.sheetStore.fetchGeneration;
  const infoBlock = sheet?.infoBlock;
  const { startTime, providerName } = infoBlock || {};
  const hasStartTime = typeof startTime === 'number' && startTime > 0;
  const hasReadableAirtime = Boolean(sheet?.readableAirTimeOrRecordingTime);
  const hasReadableExpireTime = Boolean(sheet?.readableExpireTime);
  const hasHeadline =
    typeof sheet?.descriptionHeadline === 'string' && sheet.descriptionHeadline.trim().length > 0;
  const hasDescription =
    typeof sheet?.description === 'string' && sheet.description.trim().length > 0;
  const handleEnterPress = () => {
    uiStore.toggleExtendedEpisodeDescriptionDialog(true, ExtendedDescriptionDataProvider.SHEET);
  };

  const onNavFocus = () => {
    uiStore.toggleFocus(false);
  };

  const handleKeypress = useCallback((direction: string) => {
    return direction === 'left';
  }, []);

  const { focusKey, ref, focused } = useFocusable({
    onEnterPress: handleEnterPress,
    onFocus: onNavFocus,
    isFocusBoundary: true,
    focusKey: FocusKeysSheet.FOCUS_KEY_SHEET_DESCRIPTION,
    onArrowPress: handleKeypress
  });

  return (
    <FocusContext.Provider value={focusKey}>
      <div
        className={clsx(styles['description'], { [styles['description--focused']]: focused })}
        ref={ref}
        data-testid="sheet-description"
        onClick={handleEnterPress}
      >
        {infoBlock && (
          <div className={styles['description__info']} data-testid={testIds.programsheet.info.box}>
            {(hasStartTime || hasReadableAirtime) && <SheetDescriptionInfoAirtime />}
            {providerName && <SheetDescriptionInfoMeta />}
            {hasReadableExpireTime && <SheetDescriptionInfoExpiry />}
          </div>
        )}

        {(!sheet || hasHeadline) && (
          <Typography
            className={styles['description__headline']}
            variant="body"
            component="div"
            data-testid={testIds.programsheet.content.info}
          >
            {hasHeadline ? sheet?.descriptionHeadline : <Skeleton width="50%" />}
          </Typography>
        )}

        {(!sheet || hasDescription) && (
          <Typography
            key={fetchGeneration}
            className={styles['description__body']}
            data-testid={testIds.programsheet.content.description}
            variant="body"
          >
            {hasDescription ? (
              sheet?.description
            ) : (
              <>
                <Skeleton />
                <Skeleton />
                <Skeleton width="30%" />
              </>
            )}
          </Typography>
        )}
      </div>
    </FocusContext.Provider>
  );
});

export default SheetDescription;
